// src/pages/OrderSuccess.tsx

import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import API_BASE_URL from '../utils/apiBase';

interface SessionData {
  payment_status: string;
  customer_email: string;
  amount_total: number;
  metadata?: {
    cart_items?: string;
  };
}

const OrderSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentIntentId = searchParams.get('payment_intent');
  const { clearCart } = useCart();
  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Clear the cart on successful order (only once on mount)
    clearCart();

    if (paymentIntentId) {
      fetchPaymentIntentDetails(paymentIntentId);
    } else if (sessionId) {
      fetchSessionDetails(sessionId);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, paymentIntentId]);

  const fetchSessionDetails = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/stripe/session/${id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch session details');
      }

      const data = await response.json();
      setSessionData(data);
    } catch (err) {
      console.error('Error fetching session:', err);
      setError('Unable to retrieve order details');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentIntentDetails = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/stripe/payment-intent/${id}`);

      if (!response.ok) {
        throw new Error('Failed to fetch payment details');
      }

      const data = await response.json();
      setSessionData(data);
    } catch (err) {
      console.error('Error fetching payment intent:', err);
      setError('Unable to retrieve order details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="section container">
      <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center', padding: '4rem 0' }}>
        {/* Success Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          backgroundColor: '#10b981',
          borderRadius: '50%',
          margin: '0 auto 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <svg
            width="48"
            height="48"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <h1 style={{ marginBottom: '1rem', color: '#10b981' }}>Order Successful!</h1>
        <p className="text-secondary" style={{ marginBottom: '2rem', fontSize: '1.125rem' }}>
          Thank you for your purchase!
        </p>

        {loading && (
          <p className="text-secondary" style={{ marginBottom: '2rem' }}>
            Loading order details...
          </p>
        )}

        {error && (
          <div className="card" style={{ padding: '1.5rem', marginBottom: '2rem', backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
            <p style={{ color: '#dc2626' }}>{error}</p>
          </div>
        )}

        {!loading && sessionData && (
          <div className="card" style={{ padding: '2rem', marginBottom: '2rem', textAlign: 'left' }}>
            <h3 style={{ marginBottom: '1rem' }}>Order Details</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="text-secondary">Payment Status:</span>
                <span style={{
                  fontWeight: 'bold',
                  color: sessionData.payment_status === 'paid' ? '#10b981' : '#f59e0b',
                  textTransform: 'capitalize'
                }}>
                  {sessionData.payment_status}
                </span>
              </div>
              {sessionData.customer_email && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="text-secondary">Email:</span>
                  <span style={{ fontWeight: '500' }}>{sessionData.customer_email}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                <span style={{ fontWeight: 'bold' }}>Total Amount:</span>
                <span style={{ fontWeight: 'bold', color: 'var(--color-primary)', fontSize: '1.25rem' }}>
                  ${sessionData.amount_total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="card" style={{ padding: '1.5rem', marginBottom: '2rem', backgroundColor: '#eff6ff' }}>
          <p style={{ marginBottom: '0.5rem', fontWeight: '500' }}>
            📧 Check your email
          </p>
          <p className="text-secondary" style={{ fontSize: '0.875rem' }}>
            A confirmation email with your PDF download links will be sent to you shortly.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/pdf" className="btn btn-primary">
            Browse More PDFs
          </Link>
          <Link to="/" className="btn" style={{ backgroundColor: 'white', border: '1px solid #d1d5db' }}>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccess;
